'use client';

import { Icons } from '@/components/icons';
import { Button } from '@/components/ui/button';
import { detectMediaType } from '@/components/ui/media-preview-modal/utils';
import { useKeyDown } from '@/hooks/use-keydown';
import { downloadFile } from '@/lib/api/download-file';
import { cn } from '@/lib/cn';
import useEmblaCarousel, {
  type UseEmblaCarouselType,
} from 'embla-carousel-react';
import { Dialog as DialogPrimitive } from 'radix-ui';
import { useEffect, useState } from 'react';
import dynamic from 'next/dynamic';
import { toast } from 'sonner';
import {
  closeFileGallery,
  setFileGalleryIndex,
  useFileGallery,
  useFileGalleryIndex,
  type FileGalleryObject,
} from './store';

export {
  closeFileGallery,
  openFilesInGallery,
  useFileGallery,
  useFileGalleryIndex,
  type FileGalleryObject,
  type OpenFilesInGalleryProps,
} from './store';

const ScrollablePDFDisplay = dynamic(
  () =>
    import('@/components/pdf/scrollable-pdf-display').then(
      (mod) => mod.ScrollablePDFDisplay
    ),
  {
    ssr: false,
  }
);

/**
 * How many slides on each side of the active one are kept mounted,
 * so swiping doesn't reveal an empty slide.
 */
const slideRenderDistance = 1;

/**
 * Embla's scroll is a damped spring, a lower duration overshoots more.
 * 15 keeps a slight bounce (~1% of the slide width) without settling slower.
 */
const carouselDuration = 15;

export type FileGalleryProps = {
  hideFilename?: boolean;
};

/**
 * This is a global gallery for previewing files, should only be rendered once.
 */
export function FileGallery({ hideFilename = false }: FileGalleryProps) {
  const fileGallery = useFileGallery();

  if (!fileGallery) {
    return null;
  }

  return (
    <DialogPrimitive.Root
      open
      onOpenChange={(open) => {
        if (!open) {
          closeFileGallery();
        }
      }}
    >
      <DialogPrimitive.Portal>
        <DialogPrimitive.Content
          aria-describedby={undefined}
          className='fixed inset-0 z-[100] flex min-h-0 flex-col bg-black/90 outline-none'
        >
          <FileGalleryHeader hideFilename={hideFilename} />
          <FileGalleryCarousel
            files={fileGallery.files}
            startIndex={fileGallery.startIndex}
          />
          <FileGalleryCaption />
          <FileGalleryFooter />
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

type FileGalleryCarouselProps = {
  files: Array<FileGalleryObject>;
  startIndex: number;
};

function FileGalleryCarousel({ files, startIndex }: FileGalleryCarouselProps) {
  const [emblaRef, emblaApi] = useEmblaCarousel({
    duration: carouselDuration,
    startIndex,
    // Desktop only, paginate with the buttons or arrow keys instead of dragging.
    watchDrag: false,
  });
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);

  useEffect(() => {
    if (!emblaApi) {
      return;
    }

    const onSelect = (api: NonNullable<UseEmblaCarouselType[1]>) => {
      setFileGalleryIndex(api.selectedScrollSnap());
      setCanScrollPrev(api.canScrollPrev());
      setCanScrollNext(api.canScrollNext());
    };

    onSelect(emblaApi);
    emblaApi.on('select', onSelect);
    emblaApi.on('reInit', onSelect);

    return () => {
      emblaApi.off('select', onSelect);
      emblaApi.off('reInit', onSelect);
    };
  }, [emblaApi]);

  useKeyDown('ArrowLeft', () => emblaApi?.scrollPrev());
  useKeyDown('ArrowRight', () => emblaApi?.scrollNext());

  return (
    <div className='relative min-h-0 grow'>
      <FileGalleryPaginateButton
        direction='previous'
        disabled={!canScrollPrev}
        onClick={() => emblaApi?.scrollPrev()}
      />
      <FileGalleryPaginateButton
        direction='next'
        disabled={!canScrollNext}
        onClick={() => emblaApi?.scrollNext()}
      />

      <div ref={emblaRef} className='size-full overflow-hidden'>
        <div className='flex h-full'>
          {files.map((file, index) => (
            <FileGallerySlide key={file.get} file={file} index={index} />
          ))}
        </div>
      </div>
    </div>
  );
}

type FileGalleryPaginateButtonProps = {
  direction: 'next' | 'previous';
  disabled: boolean;
  onClick: () => void;
};

function FileGalleryPaginateButton({
  direction,
  disabled,
  onClick,
}: FileGalleryPaginateButtonProps) {
  const Icon = direction === 'next' ? Icons.next : Icons.previous;

  return (
    <button
      type='button'
      aria-label={direction === 'next' ? 'Nästa fil' : 'Föregående fil'}
      className={cn(
        'absolute top-1/2 z-10 flex size-20 -translate-y-1/2 items-center justify-center text-white transition-colors hover:bg-white/10',
        direction === 'next' ? 'right-0' : 'left-0',
        disabled && 'opacity-10'
      )}
      disabled={disabled}
      onClick={onClick}
    >
      <Icon className='size-8' />
    </button>
  );
}

type FileGallerySlideProps = {
  file: FileGalleryObject;
  index: number;
};

function FileGallerySlide({ file, index }: FileGallerySlideProps) {
  const activeIndex = useFileGalleryIndex();

  // Only slides near the active one are mounted, rendering every pdf in the
  // gallery at once allocates a lot of canvas memory.
  const isNearActive = Math.abs(index - activeIndex) <= slideRenderDistance;

  return (
    <div className='flex size-full min-w-0 shrink-0 grow-0 basis-full transform-gpu items-center justify-center select-none'>
      {isNearActive && <FileGalleryItemContent file={file} />}
    </div>
  );
}

function FileGalleryHeader({ hideFilename }: { hideFilename: boolean }) {
  const fileGallery = useFileGallery();
  const activeIndex = useFileGalleryIndex();

  const total = fileGallery?.files.length ?? 0;
  const name = fileGallery?.files[activeIndex]?.name ?? '';

  return (
    <div className='relative mb-2 px-20 pt-4 text-center'>
      <div className='-mb-1.5 text-white/70'>
        Fil {activeIndex + 1} av {total}
      </div>
      <DialogPrimitive.Title
        className={cn(
          'w-full truncate text-center font-medium text-white',
          hideFilename && 'sr-only'
        )}
      >
        {name}
      </DialogPrimitive.Title>
      <DialogPrimitive.Close asChild>
        <button
          type='button'
          aria-label='Stäng'
          className='absolute top-2 right-2 flex size-12 items-center justify-center rounded-md text-white transition-colors hover:bg-white/10'
        >
          <Icons.close className='size-6' />
        </button>
      </DialogPrimitive.Close>
    </div>
  );
}

function FileGalleryCaption() {
  const fileGallery = useFileGallery();
  const activeIndex = useFileGalleryIndex();

  const file = fileGallery?.files[activeIndex];

  if (!file?.title && !file?.description) {
    return null;
  }

  return (
    <div className='mx-auto w-full max-w-3xl px-4 pt-2 text-center text-white'>
      {file.title && <p className='font-medium'>{file.title}</p>}
      {file.description && (
        <p className='line-clamp-3 text-sm whitespace-pre-line text-white/70'>
          {file.description}
        </p>
      )}
    </div>
  );
}

function FileGalleryFooter() {
  const fileGallery = useFileGallery();
  const activeIndex = useFileGalleryIndex();

  const file = fileGallery?.files[activeIndex];

  return (
    <div className='mx-auto w-full max-w-60 p-4'>
      <Button
        size='lg'
        className='w-full border-gray-800 bg-black text-white hover:bg-gray-900'
        disabled={!file?.get}
        onClick={async () => {
          if (!file?.get) {
            return;
          }
          const { success } = await downloadFile(file.get, file.name ?? 'file');
          if (!success) {
            toast.error('Kunde inte ladda ner filen');
          }
        }}
      >
        <Icons.download className='mr-2 size-4' />
        Ladda ner
      </Button>
    </div>
  );
}

function FileGalleryItemContent({ file }: { file: FileGalleryObject }) {
  if (!file.name || !file.get) {
    return null;
  }

  const { category, extension } = detectMediaType(file.get);

  if (category === 'image') {
    return (
      <div className='size-full p-4'>
        <img
          className='size-full object-contain'
          src={file.get}
          alt={file.name}
        />
      </div>
    );
  }

  if (extension === 'pdf') {
    return (
      <div className='size-full max-w-6xl [scrollbar-width:thin] [scrollbar-color:rgb(255_255_255/0.2)_transparent] overflow-y-auto px-4'>
        <ScrollablePDFDisplay url={file.get} />
      </div>
    );
  }

  return (
    <div className='flex flex-col items-center justify-center gap-1 text-white'>
      <Icons.file className='size-16' />
      <div className='w-full max-w-60 truncate px-5 text-center font-medium'>
        {file.name}
      </div>
    </div>
  );
}
